// A picture's tonal histogram, as Photoshop and Lightroom show it: 256 levels per colour channel
// plus luminance, the scale to draw them at, and whether the shadows or highlights are clipped.

export const HISTOGRAM_BINS = 256;

export type ColorChannel = 'red' | 'green' | 'blue';
export type HistogramSeries = ColorChannel | 'luminance';
/** What the histogram shows: the colour channels together, one of them, or luminance. */
export type HistogramChannel = 'rgb' | HistogramSeries;

export interface Histogram {
    red: Uint32Array;
    green: Uint32Array;
    blue: Uint32Array;
    luminance: Uint32Array;
    /** How many pixels were counted. */
    pixels: number;
    /**
     * The pixels clipped for real, to black or white: all three channels within `CLIP_LEVELS` of the
     * end. One channel at its end alone (a saturated colour) loses no detail.
     */
    clipped: { shadows: number; highlights: number };
}

/** How many levels at each end count as clipped, in all three channels at once (real clipping). */
export const CLIP_LEVELS = 5;
/** How many levels at each end count as clipped in a single colour channel's own view. */
export const CLIP_LEVELS_CLR = 1;
/** The clipping indicators light from this share of the pixels on, not for a few stray ones. */
export const CLIP_THRESHOLD = 0.005;
/** The scale stops at this many times the 98th percentile, so one spike doesn't flatten the rest. */
export const PEAK_CAP = 3;

/**
 * Counts RGBA pixel data (as `ImageData.data` holds it). Fully transparent pixels are skipped.
 * Luminance is weighted as Photoshop's luminosity histogram: 0.30 R + 0.59 G + 0.11 B.
 */
export function computeHistogram(rgba: ArrayLike<number>): Histogram {
    const red = new Uint32Array(HISTOGRAM_BINS);
    const green = new Uint32Array(HISTOGRAM_BINS);
    const blue = new Uint32Array(HISTOGRAM_BINS);
    const luminance = new Uint32Array(HISTOGRAM_BINS);
    let pixels = 0;
    const clipped = {shadows: 0, highlights: 0};
    const black = CLIP_LEVELS - 1;
    const white = HISTOGRAM_BINS - CLIP_LEVELS;
    for (let i = 0; i + 3 < rgba.length; i += 4) {
        if (rgba[i + 3] === 0) continue;
        const r = rgba[i];
        const g = rgba[i + 1];
        const b = rgba[i + 2];
        red[r]++;
        green[g]++;
        blue[b]++;
        luminance[Math.round(0.3 * r + 0.59 * g + 0.11 * b)]++;
        if (r <= black && g <= black && b <= black) clipped.shadows++;
        else if (r >= white && g >= white && b >= white) clipped.highlights++;
        pixels++;
    }
    return {red, green, blue, luminance, pixels, clipped};
}

/** The series a channel view draws. */
export function seriesOf(channel: HistogramChannel): HistogramSeries[] {
    return channel === 'rgb' ? ['red', 'green', 'blue'] : [channel];
}

/**
 * Evens out the comb a resized JPEG leaves in the counts, for drawing: each inner level becomes the
 * average of itself and `radius` neighbours either side. The two ends stay as counted, so clipping
 * still shows as a spike there.
 */
export function smoothBins(bins: ArrayLike<number>, radius = 1): number[] {
    const last = bins.length - 1;
    return Array.from(bins, (value, i) => {
        if (i === 0 || i === last) return value;
        let sum = 0;
        let count = 0;
        for (let j = Math.max(1, i - radius); j <= Math.min(last - 1, i + radius); j++) {
            sum += bins[j];
            count++;
        }
        return sum / count;
    });
}

/**
 * The count drawn at full height: the highest inner level of the series, but at most 3× their 98th
 * percentile, so a single spike (a plain background) doesn't flatten everything else. The two ends
 * are left out, since clipping piles up there and has its own indicators.
 */
export function histogramPeak(series: ArrayLike<number>[]): number {
    const inner = series.flatMap((bins) => Array.from(bins).slice(1, -1)).sort((a, b) => a - b);
    if (inner.length === 0) return 1;
    const max = inner[inner.length - 1];
    const p98 = inner[Math.floor((inner.length - 1) * 0.98)];
    return Math.max(1, Math.min(max, PEAK_CAP * p98));
}

export interface Clipping {
    /** At least `CLIP_THRESHOLD` of the pixels are clipped at this end. */
    clipped: boolean;
    /** The clipped share of the pixels, 0–1. */
    share: number;
}

/**
 * How much of the picture is clipped to black (shadows) or white (highlights) in a channel view.
 * The RGB and luminance views count real clipping, where all three channels are at the end
 * (`Histogram.clipped`); a single colour channel's view counts that channel's own end levels.
 */
export function clippingOf(histogram: Histogram, channel: HistogramChannel, end: 'shadows' | 'highlights'): Clipping {
    let count: number;
    if (channel === 'rgb' || channel === 'luminance') {
        count = histogram.clipped[end];
    } else {
        const bins = histogram[channel];
        const first = end === 'shadows' ? 0 : HISTOGRAM_BINS - CLIP_LEVELS_CLR;
        count = 0;
        for (let i = first; i < first + CLIP_LEVELS_CLR; i++) count += bins[i];
    }
    const share = count / Math.max(histogram.pixels, 1);
    return {clipped: share >= CLIP_THRESHOLD, share};
}

/** The indicator's colour: white for real clipping, the channel's own colour in a single channel's view. */
export function clippingColor(channel: HistogramChannel): string {
    if (channel === 'rgb' || channel === 'luminance') return 'rgb(255, 255, 255)';
    const level = (c: ColorChannel) => c === channel ? 255 : 0;
    return `rgb(${level('red')}, ${level('green')}, ${level('blue')})`;
}
